import { useState } from 'react';
import { toast } from 'sonner';

import { useCreateUser } from '~entities/users/api/create-user';
import { useGetUserRoles } from '~entities/users/api/get-user-roles';
import type { User } from '~entities/users/model/types';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddUserDialog = ({ open, onOpenChange }: AddUserDialogProps) => {
  const { data: enrollioRolesData, isLoading: isEnrollioRolesLoading } =
    useGetUserRoles();
  const { mutate: createUser, isPending: isCreatingUser } = useCreateUser();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  const enrollioRoles =
    enrollioRolesData?.map((role) => {
      return { name: role.name, id: role.id ?? '' };
    }) ?? [];

  const handleAddUser = () => {
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !selectedRole
    ) {
      toast.error('Please fill in all fields');
      return;
    }

    const user: User = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
    };

    createUser(
      {
        roleId: selectedRole,
        user,
      },
      {
        onSuccess: () => {
          toast.success('User added successfully');
          handleClose();
        },
        onError: (error) => {
          toast.error('Failed to create user');
          console.error('Failed to create user:', error);
        },
      },
    );
  };

  const handleClose = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setSelectedRole('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Add User to Enrollio Portal</DialogTitle>
          <DialogDescription>
            Add a new user to the Enrollio portal with appropriate permissions.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-4 py-4'>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='firstName' className='text-right'>
              First Name
            </Label>
            <Input
              id='firstName'
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
              }}
              className='col-span-3'
              placeholder='Enter first name'
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='lastName' className='text-right'>
              Last Name
            </Label>
            <Input
              id='lastName'
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
              }}
              className='col-span-3'
              placeholder='Enter last name'
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='email' className='text-right'>
              Email
            </Label>
            <Input
              id='email'
              type='email'
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              className='col-span-3'
              placeholder='Enter email address'
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='enrollioRole' className='text-right'>
              Enrollio Role
            </Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className='col-span-3'>
                <SelectValue placeholder='Select a role' />
              </SelectTrigger>
              <SelectContent>
                {enrollioRoles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type='submit'
            onClick={handleAddUser}
            disabled={
              !firstName.trim() ||
              !lastName.trim() ||
              !email.trim() ||
              !selectedRole ||
              isCreatingUser ||
              isEnrollioRolesLoading
            }
          >
            {isCreatingUser ? 'Adding...' : 'Add User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
